/* eslint-disable max-statements */
import { Button, Group, Stack, Stepper } from '@mantine/core';
import { IconFileCode, IconList, IconTemplate } from '@tabler/icons-react';
import { useState } from 'react';

import { useExportTestDefinition } from '@admin/hooks';
import { ExportStep } from './ExportStep';
import { ReviewFlowStep } from './ReviewFlowStep';
import { TemplateSelectionStep } from './TemplateSelectionStep';

import type { TestDefinition } from '@admin/hooks/request-logger/useTestGenerator';

interface IntegrationTestGeneratorViewProps {
  onRequestClick?: (requestId: string) => void;
}

export const IntegrationTestGeneratorView = ({ onRequestClick }: IntegrationTestGeneratorViewProps) => {
  const [active, setActive] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState('batch_lifecycle');
  const [batchId, setBatchId] = useState<string>('');
  const [exportedData, setExportedData] = useState<TestDefinition | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const exportTestDefinition = useExportTestDefinition();

  const handleBatchSelected = (selectedBatchId: string) => {
    setBatchId(selectedBatchId);
    setActive(1);
  };

  const handleExport = async () => {
    if (!batchId) return;

    setIsExporting(true);
    const data = await exportTestDefinition(batchId, selectedTemplate);
    setExportedData(data);
    setIsExporting(false);
    setActive(2);
  };

  const nextStep = () => setActive((current) => (current < 2 ? current + 1 : current));
  const prevStep = () => setActive((current) => (current > 0 ? current - 1 : current));

  return (
    <Stack gap="md">
      <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
        <Stepper.Step label="Select Template" description="Choose test template" icon={<IconTemplate size={18} />}>
          <TemplateSelectionStep selectedTemplate={selectedTemplate} onTemplateChange={setSelectedTemplate} onBatchSelected={handleBatchSelected} />
        </Stepper.Step>

        <Stepper.Step label="Review Flow" description="View captured requests" icon={<IconList size={18} />}>
          <ReviewFlowStep
            batchId={batchId}
            selectedTemplate={selectedTemplate}
            onExport={handleExport}
            isExporting={isExporting}
            onRequestClick={onRequestClick}
          />
        </Stepper.Step>

        <Stepper.Step label="Export" description="Download test definition" icon={<IconFileCode size={18} />}>
          <ExportStep exportedData={exportedData} />
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between" mt="xl">
        <Button variant="default" onClick={prevStep} disabled={active === 0}>
          Back
        </Button>
        {active < 2 && (
          <Button onClick={nextStep} disabled={active === 0 ? !batchId : false}>
            Next
          </Button>
        )}
      </Group>
    </Stack>
  );
};
